// src/components/auth/WelcomePage.tsx
import { GoogleSignInButton } from "@/components/auth/GoogleSignInButton";
import { EmailSignInPanel } from "@/components/auth/EmailSignInPanel";
import { GlobalSettingsControl } from "@/components/common/GlobalSettingsControl";
import { PLATFORM_DEFAULT_APP_DISPLAY_NAME } from "@/config/productDisplay";
import { THEME_OPTIONS } from "@/components/common/settingsDrawerConstants";
import { useAppTheme } from "@/contexts/ThemeContext";

export type WelcomePageVariant = "cloud" | "no-supabase";

type WelcomePageProps = {
  /** `cloud` = normal Google sign-in. `no-supabase` = env vars missing; offer dev entry only. */
  variant?: WelcomePageVariant;
  /** When variant is `no-supabase`, opens the legacy workspace without auth. */
  onContinueLocalDev?: () => void;
};

/**
 * Entry screen so anonymous visitors do not land straight in leads/training.
 * - **cloud**: Supabase is configured; Google OAuth and/or email (magic link + password) per project providers.
 * - **no-supabase**: No `VITE_SUPABASE_*` in this build — local/dev continues via explicit button.
 */
export function WelcomePage(props: WelcomePageProps) {
  const variant = props.variant ?? "cloud";
  const { theme, setTheme } = useAppTheme();

  return (
    <div className="relative min-h-screen flex flex-col bg-gradient-to-b from-slate-100 via-white to-slate-50 dark:from-slate-950 dark:via-slate-900 dark:to-slate-950">
      <div
        className="pointer-events-none absolute inset-0 overflow-hidden opacity-40 dark:opacity-25"
        aria-hidden
      >
        <div className="absolute -top-32 -right-24 h-72 w-72 rounded-full bg-indigo-400/30 blur-3xl dark:bg-indigo-500/20" />
        <div className="absolute -bottom-24 -left-16 h-80 w-80 rounded-full bg-emerald-400/25 blur-3xl dark:bg-emerald-500/15" />
      </div>

      <div className="absolute top-4 right-4 z-30 flex items-center gap-2">
        <GlobalSettingsControl />
      </div>

      <div className="relative z-10 flex flex-1 flex-col items-center justify-center px-6 py-16">
        <div className="max-w-lg w-full text-center space-y-8">
          <div className="space-y-4">
            <p className="text-[11px] font-semibold uppercase tracking-[0.2em] text-slate-500 dark:text-slate-400">
              {PLATFORM_DEFAULT_APP_DISPLAY_NAME}
            </p>
            <h1 className="text-4xl sm:text-5xl font-black tracking-tight bg-clip-text text-transparent bg-gradient-to-r from-indigo-600 via-violet-600 to-emerald-600 dark:from-indigo-400 dark:via-violet-400 dark:to-emerald-400">
              {variant === "cloud" ? "Sign in to continue" : "Local development"}
            </h1>
            {variant === "cloud" ? (
              <p className="text-sm text-slate-600 dark:text-slate-400 leading-relaxed max-w-md mx-auto">
                Sign in with Google or your work email and password. Invite links from your admin sign you in and then
                ask for a password—wait a moment after tapping the link.
              </p>
            ) : (
              <p className="text-sm text-slate-600 dark:text-slate-400 leading-relaxed max-w-md mx-auto">
                This build does not have cloud sign-in configured (missing <code className="text-xs">VITE_SUPABASE_URL</code>{" "}
                / <code className="text-xs">VITE_SUPABASE_ANON_KEY</code>). Use the button below for local development
                without auth, or configure Supabase and reload.
              </p>
            )}
          </div>

          <div className="rounded-2xl border border-slate-200/80 bg-white/70 dark:border-slate-700/80 dark:bg-slate-900/50 px-4 py-4 backdrop-blur-sm shadow-sm">
            <p className="text-[11px] uppercase tracking-wide text-slate-500 dark:text-slate-400 mb-2">Appearance</p>
            <p className="text-xs text-slate-500 dark:text-slate-500 mb-3">
              Quick toggle (also under{" "}
              <span className="font-medium text-slate-700 dark:text-slate-300" aria-hidden>
                ⚙ Settings
              </span>
              ).
            </p>
            <div className="flex flex-wrap items-center justify-center gap-2">
              {THEME_OPTIONS.map(({ value, label }) => (
                <button
                  key={value}
                  type="button"
                  onClick={() => setTheme(value)}
                  className={
                    "rounded-xl border px-3 py-1.5 text-xs font-medium transition-colors " +
                    (theme === value
                      ? "border-indigo-500 bg-indigo-50 text-indigo-900 dark:border-indigo-400 dark:bg-indigo-950/60 dark:text-indigo-100"
                      : "border-slate-200 bg-white text-slate-700 hover:bg-slate-50 dark:border-slate-600 dark:bg-slate-800 dark:text-slate-300 dark:hover:bg-slate-700/80")
                  }
                >
                  {label}
                </button>
              ))}
            </div>
          </div>

          {variant === "cloud" ? (
            <div className="space-y-6 w-full max-w-md mx-auto">
              <div className="flex justify-center">
                <GoogleSignInButton className="rounded-2xl border-2 border-indigo-200 dark:border-indigo-800 bg-white px-6 py-3 text-sm font-semibold text-indigo-900 shadow-md hover:bg-indigo-50 dark:bg-indigo-950/40 dark:text-indigo-100 dark:hover:bg-indigo-900/60" />
              </div>
              <EmailSignInPanel />
            </div>
          ) : (
            <div className="flex justify-center">
              <button
                type="button"
                onClick={() => props.onContinueLocalDev?.()}
                className="rounded-2xl border border-slate-800 bg-slate-900 px-6 py-3 text-sm font-semibold text-white shadow-md hover:bg-slate-800 dark:border-slate-500 dark:bg-slate-800"
              >
                Continue to workspace (local development)
              </button>
            </div>
          )}

          <p className="text-xs text-slate-500 dark:text-slate-500 max-w-md mx-auto">
            {variant === "cloud"
              ? "Need an account? Ask your administrator to invite your work email."
              : "This bypass is remembered on this device until you clear site data for this origin."}
          </p>
          <p className="text-xs text-slate-500 dark:text-slate-500">
            <a className="underline underline-offset-2 hover:text-slate-700 dark:hover:text-slate-300" href="/privacy">
              Privacy policy
            </a>
          </p>
        </div>
      </div>
    </div>
  );
}
